<template>
  <div class="table-wrapper">
    <table>
      <caption class="visually-hidden">
        {{ $t('CALL_LOG_REPORT.HEADER') }}
      </caption>
      <thead>
        <tr>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.INBOX') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.DATE') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.DURATION') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.STATUS') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.CONTACT') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.PHONE') }}</th>
          <th>{{ $t('CALL_LOG_REPORT.TABLE.LINK') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(callLog, index) in paginatedData"
          :key="callLog.id"
          :class="{ 'striped-row': index % 2 !== 0 }"
        >
          <td>{{ callLog.inbox_name || '-' }}</td>
          <td>{{ formatDate(callLog.created_at) }}</td>
          <td>{{ callLog.duration_formatted || '-' }}</td>
          <td>
            <span 
              class="status-badge" 
              :class="getStatusClass(callLog.call_status)"
            >
              {{ callLog.status_message }}
            </span>
          </td>
          <td>{{ callLog.contact_name || '-' }}</td>
          <td>{{ callLog.phone_number || '-' }}</td>
          <td>
            <a 
              v-if="callLog.link_conversation" 
              :href="callLog.link_conversation" 
              target="_blank"
              rel="noopener noreferrer"
              :title="callLog.link_conversation"
            >
              {{ truncate(callLog.link_conversation) }}
            </a>
            <span v-else>-</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!callLogsData.length" class="no-data">
      {{ $t('CALL_LOG_REPORT.TABLE.NO_DATA') }}
    </div>

    <div class="pagination">
      <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
        {{ $t('CALL_LOG_REPORT.PAGINATION.PREVIOUS') }}
      </button>
      <span>
        {{ $t('CALL_LOG_REPORT.PAGINATION.PAGE') }} {{ currentPage }}
        {{ $t('CALL_LOG_REPORT.COMMON.OF') }} {{ totalPages }}
      </span>
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
      >
        {{ $t('CALL_LOG_REPORT.PAGINATION.NEXT') }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CallLogTable',
  props: {
    callLogsData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    sortedData() {
      return [...this.callLogsData].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    },
    totalPages() {
      return Math.ceil(this.sortedData.length / this.perPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedData.slice(start, end);
    },
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getStatusClass(status) {
      const statusClasses = {
        completed: 'status-success',
        initiated: 'status-info',
        answered: 'status-warning',
        failed: 'status-error',
      };
      return statusClasses[status] || 'status-default';
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    truncate(text, maxLength = 30) {
      if (!text) return '';
      return text.length > maxLength 
        ? `${text.substring(0, maxLength)}...` 
        : text;
    },
  },
  watch: {
    callLogsData() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
}

thead {
  background-color: #2563eb;
  color: white;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eaeaea;
  word-break: break-word;
  vertical-align: top;
}

.striped-row {
  background-color: #f9f9f9;
}

a {
  color: #2563eb;
  text-decoration: underline;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 16px;
  align-items: center;
}

.pagination button {
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

td span {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;

  &.status-success {
    background: #d1fae5;
    color: #065f46;
  }

  &.status-error {
    background: #fee2e2;
    color: #991b1b;
  }

  &.status-warning {
    background: #fef3c7;
    color: #92400e;
  }

  &.status-default {
    background: #f3f4f6;
    color: #374151;
  }
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-style: italic;
  background: white;
}
</style>